// Zone fixtures: the zone list, per-zone rate config (tiers, passes, recurring
// plans), event rates, the live checkout preview, and nearby events. All joined
// on the active client's zones so the operator story is consistent across the
// Zones screen tabs.

import type {
  ZoneRates,
  ZoneBay, ZoneBayCreateResponse, ZoneBaysResponse, ZoneBayUpdateResponse,
  ZoneClosure, ZoneClosuresResponse, ZoneClosuresSaveResponse, ZoneReadinessSummaryResponse,
  ZonePlatformFeeResponse,
} from '@parkify/api-types'
import {
  DEMO_ZONES, DEMO_CLIENT, DEMO_CLIENT_ID, DEMO_TIMEZONE, ZONE_NORTH,
  zoneById, type DemoZoneMeta,
} from '../constants'
import { ZONE_MAIN, ZONE_VIP } from '../constants'
import { isoDaysFromNow, isoDaysAgoAt, dateOnlyDaysFromNow } from '../time'

type DemoGoLiveRow = {
  key: string
  label: string
  source: 'derived' | 'milestone'
  done: boolean
  owner?: 'parkify' | 'client' | 'both'
  targetDate?: string
  notes?: string
  doneAt?: string
}

// GET /api/zones  → full zone records.
/**
 * `GET /api/zones/picker` — the lean lot list the Integrations attach modal reads.
 *
 * Derived from the same DEMO_ZONES as `demoZonesList` so the picker and the Zones
 * screen can never disagree about which lots exist, and projected to the same
 * three-and-a-bit fields the real route returns (id + display code + location).
 */
export function demoZonePicker() {
  return DEMO_ZONES.map((z) => ({
    id: z.id,
    name: z.displayName ?? z.name,
    address: z.address ?? null,
    city: DEMO_CLIENT.city ?? null,
    state: DEMO_CLIENT.state ?? null,
  })).sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * `GET /api/zones/directory` — the zone list without `customQrUrl`, which every
 * screen that only reads zones fetches. The demo list carries no QR artwork, so
 * it is the same records; stripped anyway so the fixture keeps the real shape if
 * one is ever added.
 */
export function demoZoneDirectory() {
  return demoZonesList().map((z) => {
    const { customQrUrl: _artwork, ...rest } = z as typeof z & { customQrUrl?: string }
    return rest
  })
}

/**
 * `?demoRailPad=N`: N extra, fully configured lots whose codes sort BEFORE the
 * demo lots (AA101…), for a rail longer than the board's follow-up window. The
 * real board's last lots alphabetically (TX702, TX714, WA2217) were the ones
 * past the summary cap, so the unchecked rows sit at the END of a long rail.
 */
const PAD_PREFIX = 'zone_pad_'
function demoRailPad(): DemoZoneMeta[] {
  if (typeof window === 'undefined') return []
  const n = Number(new URL(window.location.href).searchParams.get('demoRailPad'))
  if (!Number.isInteger(n) || n <= 0) return []
  return Array.from({ length: Math.min(n, 80) }, (_, i): DemoZoneMeta => ({
    ...DEMO_ZONES[0],
    id: `${PAD_PREFIX}${String(i + 1).padStart(2, '0')}`,
    name: `AA${101 + i}`,
    displayName: `AA${101 + i}`,
    zoneNumber: String(101 + i),
  }))
}

export function demoZonesList() {
  return [...demoRailPad(), ...DEMO_ZONES].map((z) => ({
    id: z.id,
    name: z.name,
    displayName: z.displayName,
    zoneNumber: z.zoneNumber,
    type: z.type,
    address: z.address,
    city: DEMO_CLIENT.city,
    state: DEMO_CLIENT.state,
    zipCode: DEMO_CLIENT.zipCode,
    description: z.description,
    clientId: DEMO_CLIENT_ID,
    lat: z.lat,
    lng: z.lng,
    timezone: DEMO_TIMEZONE,
    operatingHours: z.operatingHours,
    totalSpaces: z.totalSpaces,
    transientCapacity: z.transientCapacity,
    monthlyCapacity: z.monthlyCapacity,
    active: true,
    facilityId: z.facilityId,
    facilityName: z.facilityId
      ? DEMO_CLIENT.facilities.find(facility => facility.id === z.facilityId)?.name
      : undefined,
    graceMinutes: 15,
    photos: [],
    createdAt: isoDaysFromNow(-180),
    updatedAt: isoDaysFromNow(-5),
  }))
}

// GET /api/zones?id=<id>  → single zone (used by Avery photo proposal).
export function demoZoneById(id: string) {
  return demoZonesList().find((z) => z.id === id) ?? null
}

/**
 * GET /api/zones/:id/readiness → the setup check + go-live checklist.
 *
 * The demo tenant's lots are fully configured, so every derived check passes and
 * the interesting half is the checklist: milestones part-done, owners and dates
 * assigned, which is what an onboarding call actually looks like mid-flight.
 *
 * `goLive` mirrors api/src/zones/readiness.ts — derived rows first, then the
 * manual milestones, then advisory. Kept in the same order the panel renders so
 * the demo reads like the real screen.
 */
export function demoZoneReadiness(id: string) {
  const zone = demoZoneById(id)
  if (!zone) return null
  // Boundary-case lenses keep the demo able to show the same operator
  // decisions as live without editing source fixtures between calls:
  // `?demoReadinessState=setup-pending|inactive|closed|unknown|payout-blocked|mitte-only`
  // (`capped` and `capped-tail` are read by demoZoneReadinessSummary below, not here).
  const lens = typeof window !== 'undefined'
    ? new URL(window.location.href).searchParams.get('demoReadinessState')
    : null
  // mitte-only: no native rates, guests pay on Mitte by design (GA569's shape).
  const mitteOnly = lens === 'mitte-only'
  const setupPending = lens === 'setup-pending' || mitteOnly
  const payoutBlocked = lens === 'payout-blocked'
  const unknown = lens === 'unknown'
  const inactive = lens === 'inactive'
  const closed = lens === 'closed'
  const derived = (key: string, label: string, done: boolean, severity: 'blocking' | 'advisory') =>
    ({ key, label, detail: `${label} — demo fixture.`, severity, ok: done })
  const items = [
    derived('id', 'Zone has a real id', true, 'blocking'),
    derived('payable', 'At least one rate, pass, or monthly plan', !setupPending && !unknown, 'blocking'),
    // Same row the Worker emits from the charge routing (readiness.ts), so the
    // payout-blocked lens reads not-ready here exactly as it does live.
    derived('can-take-payment', 'Payment acceptance check', !payoutBlocked && !unknown, 'blocking'),
    derived('timezone', 'Timezone set explicitly', true, 'blocking'),
    derived('address', 'Full street address', (!setupPending || mitteOnly) && !unknown, 'blocking'),
    derived('geocode', 'Coordinates geocoded', true, 'advisory'),
    derived('capacity', 'Total spaces recorded', true, 'advisory'),
    derived('public-listing', 'Listed in the public directory', false, 'advisory'),
  ]
  const row = (
    key: string,
    label: string,
    source: DemoGoLiveRow['source'],
    done: boolean,
    extra: Partial<Omit<DemoGoLiveRow, 'key' | 'label' | 'source' | 'done'>> = {},
  ): DemoGoLiveRow => ({ key, label, source, done, ...extra })
  return {
    zoneId: zone.id,
    displayName: zone.displayName,
    configurationState: unknown ? 'unknown' as const
      : items.some(item => item.severity === 'blocking' && !item.ok) ? 'blocked' as const : 'ready' as const,
    ready: !unknown && !items.some(item => item.severity === 'blocking' && !item.ok),
    // ⚠️ Derived from the CLOSURE fixtures below, not hard-coded to 'accepting'.
    // `demoZoneClosures` puts a running all-day closure on ZONE_MAIN, and the
    // live server resolves `trading` through the same money-in gate that closure
    // feeds — so a hard-coded green band would make the demo contradict itself
    // across two tabs of one zone, which is the single most noticeable kind of
    // wrong on a sales call.
    trading: inactive
      ? {
          state: 'paused' as const, acceptsNewCheckoutPayments: false, acceptsNewMonthlySignups: false,
          existingSessionsUnaffected: true as const, existingSubscriptionsUnaffected: true as const,
          pausedBy: 'inactive' as const,
          detail: 'Paused because this zone is inactive: new checkout payments and monthly sign-ups are refused. Running sessions and existing subscriptions continue.',
        }
      : (closed
        ? {
            state: 'paused' as const, acceptsNewCheckoutPayments: false, acceptsNewMonthlySignups: false,
            existingSessionsUnaffected: true as const, existingSubscriptionsUnaffected: true as const,
            pausedBy: 'closed' as const,
            detail: 'Paused by a closure (see Closures on the zone Overview): new checkout payments and monthly sign-ups are refused for this period. Running sessions and existing subscriptions continue.',
          }
        : demoZoneTrading(zone.id)),
    blocking: items.filter(item => !item.ok && item.severity === 'blocking').length,
    advisory: items.filter(item => !item.ok && item.severity === 'advisory').length,
    items,
    // The demo operator is a normal Connect client whose lots follow its default
    // payout entity — the most common real arrangement, and the one whose
    // shared-fate warning is worth showing on a demo call.
    payout: payoutBlocked
      ? { kind: 'blocked' as const, via: 'unresolved' as const, reason: 'Payout destination could not be resolved for this zone.' }
      : { kind: 'connect' as const, via: 'client-default' as const, clientName: DEMO_CLIENT.name, entityName: `${DEMO_CLIENT.name} LLC` },
    // Same derivation as the Worker's guestPayState: read off the fixture's
    // own rate and payout facts, so the demo's flag and card cannot disagree.
    guestPay: unknown
      ? { state: 'unknown' as const, detail: 'Guest checkout or payment routing was not verified on this read.' }
      : mitteOnly
        ? { state: 'mitte-only' as const, blockerKey: 'payable', label: 'Mitte-only', detail: 'Guests pay on Mitte (parkingewallet.com): this lot\'s pay link points there and native checkout is not set up. Not a fault while the lot stays on Mitte; it becomes one if the pay link moves to pay.parkify.com.' }
      : setupPending
        ? { state: 'blocked' as const, blockerKey: 'payable', label: 'Awaiting rates', detail: 'With no payable config, checkout resolves "unavailable" — the QR scans but no one can pay.' }
        : payoutBlocked
          ? { state: 'blocked' as const, blockerKey: 'can-take-payment', label: 'Awaiting Stripe', detail: 'Checkout refuses every charge on this lot right now: Payout destination could not be resolved for this zone.' }
          : { state: 'open' as const, detail: 'The live checkout offers a price and a charge can route.' },
    notes: [
      'Switching this zone Inactive stops it taking money: new checkout quotes and new monthly sign-ups are refused, not merely hidden from listings.',
      'Three things stay payable on an Inactive zone, deliberately: extending a session already running, renewals on existing monthly subscriptions, and paying off an enforcement notice. Cancelling a monthly is its own flow, not a side effect of this toggle.',
    ],
    goLive: [
      row('id', 'Zone has a real id', 'derived', true),
      row('payable', 'At least one rate, pass, or monthly plan', 'derived', true, { owner: 'parkify' }),
      row('timezone', 'Timezone set explicitly', 'derived', true),
      row('address', 'Full street address', 'derived', true),
      row('stripe-setup', 'Stripe setup completed', 'milestone', true, { owner: 'parkify', targetDate: dateOnlyDaysFromNow(-14), doneAt: isoDaysFromNow(-13) }),
      row('signage-ordered', 'Signage ordered', 'milestone', true, { owner: 'client', targetDate: dateOnlyDaysFromNow(-7), doneAt: isoDaysFromNow(-6), notes: 'standard package' }),
      row('signage-installed', 'Signage installed on-site', 'milestone', false, { owner: 'client', targetDate: dateOnlyDaysFromNow(9) }),
      row('billing-begins', 'Billing begins', 'milestone', false, { owner: 'both', targetDate: dateOnlyDaysFromNow(16) }),
      row('geocode', 'Coordinates geocoded', 'derived', true),
      row('capacity', 'Total spaces recorded', 'derived', true),
      row('public-listing', 'Listed in the public directory', 'derived', false, { owner: 'parkify', notes: 'QR-only for now' }),
    ],
  }
}

/** GET /api/zones/readiness-summary → one bounded portfolio response. */
export function demoZoneReadinessSummary(): ZoneReadinessSummaryResponse {
  // `?demoReadinessState=capped`: every lot sits past the summary's setup-probe
  // cap, as on a board over SUMMARY_SETUP_PROBE_CAP lots. Ready rows come back
  // `unverified` here while each lot's own readiness read stays ready, which is
  // what the board's per-lot follow-up (zones/boardSetupFollowUp.ts) resolves.
  // `capped-tail`: only the demo lots (the END of a `demoRailPad` rail) are past
  // the cap, as TX702, TX714 and WA2217 were; the padding lots were checked.
  const lens = typeof window !== 'undefined'
    ? new URL(window.location.href).searchParams.get('demoReadinessState')
    : null
  const zones = demoZonesList().map(zone => {
    const detail = demoZoneReadiness(zone.id)!
    const capped = lens === 'capped' || (lens === 'capped-tail' && !zone.id.startsWith(PAD_PREFIX))
    const unverified = capped && detail.configurationState === 'ready'
    const today = new Date().toISOString().slice(0, 10)
    const overdue = detail.goLive.filter(row => !row.done && row.targetDate && row.targetDate < today).length
    const next = detail.goLive
      .filter(row => !row.done)
      .slice()
      .sort((a, b) => (a.targetDate ?? '9999-12-31').localeCompare(b.targetDate ?? '9999-12-31'))[0]
    return {
      zoneId: zone.id,
      clientId: zone.clientId,
      displayName: zone.displayName,
      configurationState: unverified ? 'unverified' as const : detail.configurationState,
      trading: detail.trading,
      guestPay: unverified
        ? { state: 'unknown' as const, detail: 'Guest checkout was not checked on this board (limit 40 zones per load). Open the zone to check it.' }
        : detail.guestPay,
      blocking: detail.blocking,
      advisory: detail.advisory,
      checklist: {
        state: 'known' as const,
        done: detail.goLive.filter(row => row.done).length,
        total: detail.goLive.length,
        overdue,
      },
      nextWork: next ? {
        key: next.key,
        label: next.label,
        owner: next.owner,
        targetDate: next.targetDate,
      } : undefined,
      // The demo clients pass the card fee through on top and set no percentage
      // service charge, so their lots read "Fee on top" (no terms). The real
      // answer is computed server-side in api/src/zones/fee_mode.ts.
      feeMode: { placement: DEMO_CLIENT.customerFees.serviceFeePassThrough || DEMO_CLIENT.customerFees.adminFeeCents > 0 ? 'on_top' as const : 'none' as const },
    }
  })
  return {
    generatedAt: new Date().toISOString(),
    zones,
    clients: [{
      clientId: DEMO_CLIENT_ID,
      zonesReady: zones.filter(zone => zone.configurationState === 'ready').length,
      zonesTotal: zones.length,
      zonesUnknown: zones.filter(zone => zone.configurationState === 'unknown').length,
      zonesUnverified: zones.filter(zone => zone.configurationState === 'unverified').length,
      overdue: zones.reduce((sum, zone) => sum + zone.checklist.overdue, 0),
      topBlockers: [],
      nextWork: zones[0]?.nextWork ? {
        zoneId: zones[0].zoneId,
        displayName: zones[0].displayName,
        ...zones[0].nextWork,
      } : undefined,
    }],
  }
}

// Recurring plan(s) + named passes for one zone — shared by rates + checkout.
function plansFor(z: DemoZoneMeta) {
  return [
    {
      id: `${z.id}-plan`,
      label: DEMO_CLIENT.recurringPlanLabel,
      amountCents: DEMO_CLIENT.monthlyPlanCentsByType[z.type],
      active: true,
    },
  ]
}

function passesFor() {
  // Named passes are operator-wide in the demo; surfaced on every zone.
  //
  // Two things here must match the REAL `Pass` shape (zones/types.ts), not just
  // DemoPass, because the rates editor consumes these as live Pass records:
  //
  //  • `validity` is REQUIRED on Pass. Omitting it crashed the whole "Advanced —
  //    edit pricing manually" panel: passValidityMode(p.validity) dereferences
  //    .kind on undefined, and the throw takes out PassesEditor and every sibling
  //    card in that panel with it. A day-long pass is 1440 minutes.
  //  • `id` must NOT carry the `pass_` prefix. The prefix belongs to the derived
  //    rateId (`pass_<id>`); baking it into the id itself both double-prefixes and
  //    trips isValidPassId, which rejects underscores — surfacing a bogus
  //    "invalid id" blocking error on Save in demo.
  return DEMO_CLIENT.passes.map(p => ({
    id: p.id,
    label: p.label,
    sublabel: p.sublabel,
    amountCents: p.amountCents,
    validity: { kind: 'duration' as const, minutes: 1440 },
    active: true,
  }))
}

// GET /api/rates?zoneId=<id>  → rate config for one zone.
export function demoRates(zoneId: string): ZoneRates {
  const z = zoneById(zoneId) ?? DEMO_ZONES[0]
  return {
    zoneId: z.id,
    name: `${z.name} rates`,
    tiers: [
      {
        id: `${z.id}-incremental`, type: 'graduated', amountCents: z.hourlyCents,
        label: 'Standard incremental', choices: z.rateChoices,
        steps: [
          { uptoMinutes: 60, flatCents: z.hourlyCents },
          { uptoMinutes: null, perHourCents: z.additionalHourCents },
        ],
      },
    ],
    dailyMaxCents: z.dailyMaxCents,
    monthlyPlans: plansFor(z),
    passes: passesFor(),
    updatedAt: isoDaysFromNow(-5),
  }
}

// GET /api/zones/<id>/checkout  → live consumer-facing price preview.
export function demoCheckout(zoneId: string) {
  const z = zoneById(zoneId) ?? DEMO_ZONES[0]
  const amountFor = (minutes: number) => z.hourlyCents
    + Math.round(z.additionalHourCents * Math.max(0, minutes - 60) / 60)
  // The checkout contract's pass validity is already presentation copy; the
  // rates record above intentionally keeps the structured authoring object.
  // Reusing that object here makes RatePreview try to render {kind, minutes}
  // as a React child and crashes the entire Rates tab in demo mode.
  const checkoutPasses = passesFor().map(p => ({ ...p, validity: 'Valid for 24 hours' }))
  return {
    mode: 'graduated',
    rateLabel: `$${(z.hourlyCents / 100).toFixed(2)} first hour · $${(z.additionalHourCents / 100).toFixed(2)}/hr after · $${(z.dailyMaxCents / 100).toFixed(0)} daily max`,
    // Production keeps an exact-max authored product and suppresses only raw
    // prices strictly above the maximum. Project from the same stable choices
    // returned by demoRates so the editor and its live preview cannot disagree.
    durations: z.rateChoices.flatMap(choice => {
      const amountCents = amountFor(choice.durationMins)
      if (amountCents > z.dailyMaxCents) return []
      const hours = choice.durationMins / 60
      const label = `${hours} hour${hours === 1 ? '' : 's'}`
      return [{
        key: choice.choiceId, choiceId: choice.choiceId,
        rateId: `${z.id}-incremental`, label, minutes: choice.durationMins,
        amountCents, totalCents: amountCents,
      }]
    }),
    dailyMaxCents: z.dailyMaxCents,
    passes: checkoutPasses,
    monthlyPlans: plansFor(z),
    feesApplied: true,
  }
}

// GET /api/event-rates?zoneId=<id>  → event-day flat-rate overrides. Only the
// primary + premium lots carry standing event rates; the overflow lot doesn't.
export function demoEventRates(zoneId: string) {
  const z = zoneById(zoneId) ?? DEMO_ZONES[0]
  if (z.id === ZONE_NORTH) return []
  return DEMO_CLIENT.eventRates.map(e => ({
    id: `${z.id}-${e.idSuffix}`,
    zoneId: z.id,
    eventTitle: e.eventTitle,
    startDateTime: isoDaysFromNow(e.daysFromNow),
    endDateTime: isoDaysFromNow(e.daysFromNow + e.durationDays),
    amountCents: z.eventFlatCents,
    tiers: [],
  }))
}

// GET /api/events?zoneId=<id>  → nearby events from the market's own calendar.
// The Worker's wire shape (rates/events.ts fetchNearbyEvents): dated occurrences
// with real instants plus the `source` that answered. The fixture used to return
// the retired SerpAPI array, which the console reads as "no event calendar is
// configured for this market", so the demo never showed Use as rate.
export function demoNearbyEvents() {
  const primary = DEMO_ZONES[0]
  return {
    events: DEMO_CLIENT.upcomingEvents.map(e => {
      const startsAt = demoWallInstant(e.daysFromNow, e.when)
      return {
        title: e.title,
        startsAt,
        endsAt: new Date(new Date(startsAt).getTime() + 3 * 3600_000).toISOString(),
        venue: DEMO_CLIENT.name,
        address: `${primary.address}, ${DEMO_CLIENT.city}, ${DEMO_CLIENT.state}`,
        link: 'https://example.com/events',
        recurring: false,
      }
    }),
    cached: true,
    location: `${DEMO_CLIENT.city}, ${DEMO_CLIENT.state}`,
    source: { id: 'demo-calendar', label: `${DEMO_CLIENT.city} events calendar`, homepage: 'https://example.com/events' },
    windowDays: 30,
  }
}

// `daysFromNow` at the fixture's "7:30 PM" wall clock, in the browser's own
// clock (a demo runs where the rep is). Deliberately not utils/datetime's zone
// conversion: these fixtures ship in the console's entry chunk, and that import
// alone cost 4.8 kB of the entry budget.
function demoWallInstant(daysFromNow: number, when: string): string {
  const m = /^(\d{1,2}):(\d{2})\s*([AP]M)$/i.exec(when.trim())
  if (!m) return isoDaysFromNow(daysFromNow)
  const d = new Date(isoDaysFromNow(daysFromNow))
  d.setHours((Number(m[1]) % 12) + (m[3]!.toUpperCase() === 'PM' ? 12 : 0), Number(m[2]), 0, 0)
  return d.toISOString()
}

// GET /api/zone-closures?zoneId=<id>  → Closures on the zone Overview.
//
// The demo seed conforms to the same ZoneClosure wire type the Worker returns.
//
// Three fixtures because the demo has to show the three shapes an operator has
// to be able to TELL APART on sight, which one closure cannot: a running one-off
// (amber Running pill), a standing weekly series (Skip today), and a reserved-lot
// closure with enforcement left ON (the red ENFORCED pill). The enforce-ON one is
// the whole reason that pill is red — it is the surprising state.
//
// Dates are derived from the clock, never pinned: a fixture pinned to a wall-clock
// window silently stops being "running" and the demo shows a Scheduled pill
// forever, with every test still green.
const DEMO_CLOSURES: Record<string, ZoneClosure[]> = {
  [ZONE_MAIN]: [
    {
      id: 'CL-1001', zoneId: ZONE_MAIN,
      reasonKind: 'Private event',
      note: 'Grace asked — corporate hire, invoice separately.',
      enforce: false, allDay: true,
      startYMD: dateOnlyDaysFromNow(0), endYMD: dateOnlyDaysFromNow(0),
      from: null, to: null,
      repeat: null, skips: [], cancelled: false, endedAtISO: null,
      createdAt: isoDaysFromNow(-6),
    },
    {
      id: 'CL-1002', zoneId: ZONE_MAIN,
      reasonKind: 'Reserved',
      enforce: false, allDay: false,
      startYMD: dateOnlyDaysFromNow(0), endYMD: dateOnlyDaysFromNow(0),
      from: '07:00', to: '10:00',
      // 0 = Sunday … 6 = Saturday. Indefinite: `untilYMD: null`.
      repeat: { days: [0], untilYMD: null },
      skips: [], cancelled: false, endedAtISO: null,
      createdAt: isoDaysFromNow(-30),
    },
  ],
  [ZONE_VIP]: [
    {
      id: 'CL-1003', zoneId: ZONE_VIP,
      reasonKind: 'Maintenance',
      note: 'Resurfacing — contractor confirmed.',
      // The reserved-lot case: the lot is shut, but an unauthorized car should
      // STILL be flagged. This is the state the red pill exists for.
      enforce: true, allDay: false,
      startYMD: dateOnlyDaysFromNow(4), endYMD: dateOnlyDaysFromNow(5),
      from: '06:00', to: '18:00',
      repeat: null, skips: [], cancelled: false, endedAtISO: null,
      createdAt: isoDaysFromNow(-2),
    },
  ],
}

/** The `trading` band for a zone, derived from its closure fixtures so the
 *  readiness band and the Closures card on Overview cannot disagree. */
function demoZoneTrading(zoneId: string) {
  const closed = (DEMO_CLOSURES[zoneId] ?? []).some((c) => {
    const x = c as { allDay?: boolean; startYMD?: string; cancelled?: boolean }
    // The demo's own "running today, all day" shape — the only one that reads as
    // paused right now. A timed or future window is Scheduled, not Running.
    return !x.cancelled && x.allDay === true && x.startYMD === dateOnlyDaysFromNow(0)
  })
  return closed
    ? {
        state: 'paused' as const,
        acceptsNewCheckoutPayments: false,
        acceptsNewMonthlySignups: false,
        existingSessionsUnaffected: true as const,
        existingSubscriptionsUnaffected: true as const,
        pausedBy: 'closed' as const,
        detail: 'Paused by a closure (see Closures on the zone Overview): new checkout payments and monthly sign-ups are refused for this period. Running sessions and existing subscriptions continue.',
      }
    : {
        state: 'accepting' as const,
        acceptsNewCheckoutPayments: true,
        acceptsNewMonthlySignups: true,
        existingSessionsUnaffected: true as const,
        existingSubscriptionsUnaffected: true as const,
        detail: 'Active: this zone is accepting new checkout payments and monthly sign-ups.',
      }
}

export function demoZoneClosures(zoneId: string): ZoneClosuresResponse {
  const z = zoneById(zoneId) ?? DEMO_ZONES[0]
  return {
    zoneId: z.id,
    // The out-of-hours enforcement flag, off — the default, and the one the
    // console now lets Parkify staff change.
    hoursEnforce: false,
    closures: DEMO_CLOSURES[z.id] ?? [],
    unreadable: false,
  }
}

/** The demo deliberately does not persist edits; an empty written list is explicit. */
export function demoSaveZoneClosures(zoneId: string): ZoneClosuresSaveResponse {
  return { ...demoZoneClosures(zoneId), written: [] }
}

// ─── Bay inventory (reserved bays phase 1) ──────────────────────────────────
// Mirrors GET /api/zone-bays. The demo keeps a per-session in-memory inventory
// so add / edit / retire / delete round-trip the way the console expects; a
// reload restores the seed below. ZONE_NORTH starts empty so the empty state
// is reachable in the demo.
export type DemoZoneBay = ZoneBay
const seedBays = (zoneId: string, section: string, labels: string[], over: Partial<DemoZoneBay> = {}): DemoZoneBay[] =>
  labels.map((label, i) => ({
    id: `bay_${zoneId}_${section.replace(/\W+/g, '').toLowerCase()}_${i + 1}`, zoneId, clientId: DEMO_CLIENT_ID, label, section,
    state: 'active', note: null, createdAt: isoDaysAgoAt(40, 9, 0), assigned: false, ...over,
  }))
const BAY_SEED: Record<string, DemoZoneBay[]> = {
  [ZONE_MAIN]: [
    ...seedBays(ZONE_MAIN, 'Level 1', ['1-01', '1-02', '1-03', '1-04', '1-05', '1-06']),
    ...seedBays(ZONE_MAIN, 'Level 2', ['2-01', '2-02', '2-03', '2-04']),
    ...seedBays(ZONE_MAIN, 'Level 2', ['2-05'], { state: 'out_of_service', note: 'Column repair — reopens Oct 3' }),
  ],
  [ZONE_VIP]: seedBays(ZONE_VIP, 'Reserved row', ['R1', 'R2', 'R3', 'R4']),
}
const bayStore = new Map<string, DemoZoneBay[]>()
function baysFor(zoneId: string): DemoZoneBay[] {
  const z = zoneById(zoneId) ?? DEMO_ZONES[0]
  if (!bayStore.has(z.id)) bayStore.set(z.id, (BAY_SEED[z.id] ?? []).map(b => ({ ...b })))
  return bayStore.get(z.id)!
}
const normalizeBayLabel = (raw: string) => raw.trim().replace(/\s+/g, ' ').toUpperCase()
function summariseBays(zoneId: string): ZoneBaysResponse {
  const bays = [...baysFor(zoneId)].sort((a, b) => (a.section ?? '~').localeCompare(b.section ?? '~') || a.label.localeCompare(b.label))
  return {
    zoneId: (zoneById(zoneId) ?? DEMO_ZONES[0]).id,
    bays,
    counts: {
      total: bays.length,
      active: bays.filter(b => b.state === 'active').length,
      outOfService: bays.filter(b => b.state === 'out_of_service').length,
      assigned: bays.filter(b => b.assigned).length,
    },
  }
}
export function demoZoneBays(zoneId: string): ZoneBaysResponse { return summariseBays(zoneId) }
export function demoAddZoneBays(body: { zoneId?: string; labels?: unknown; label?: unknown; section?: unknown; note?: unknown }): ZoneBayCreateResponse {
  const zoneId = body.zoneId || DEMO_ZONES[0].id
  const raw: unknown[] = Array.isArray(body.labels) ? body.labels : typeof body.labels === 'string' ? body.labels.split(/[\n,;]+/) : typeof body.label === 'string' ? [body.label] : []
  const list = baysFor(zoneId)
  const have = new Set(list.map(b => b.label))
  const added: string[] = []; const existing: string[] = []; const rejected: string[] = []
  for (const part of raw) {
    if (typeof part !== 'string' || !part.trim()) continue
    const label = normalizeBayLabel(part)
    if (!label || label.length > 24) { rejected.push(part.trim()); continue }
    if (have.has(label)) { if (!existing.includes(label)) existing.push(label); continue }
    have.add(label); added.push(label)
    list.push({
      id: `bay_demo_${Date.now().toString(36)}_${added.length}`, zoneId, clientId: DEMO_CLIENT_ID, label,
      section: typeof body.section === 'string' && body.section.trim() ? body.section.trim() : null,
      state: 'active', note: typeof body.note === 'string' && body.note.trim() ? body.note.trim() : null,
      createdAt: new Date().toISOString(), assigned: false,
    })
  }
  return { ...summariseBays(zoneId), added, existing, rejected }
}
export function demoUpdateZoneBay(body: { zoneId?: string; bayId?: string; label?: unknown; section?: unknown; note?: unknown; state?: unknown }): ZoneBaysResponse | ZoneBayUpdateResponse {
  const zoneId = body.zoneId || DEMO_ZONES[0].id
  const bay = baysFor(zoneId).find(b => b.id === body.bayId)
  if (!bay) return summariseBays(zoneId)
  if (typeof body.label === 'string' && normalizeBayLabel(body.label)) bay.label = normalizeBayLabel(body.label)
  if (body.section !== undefined) bay.section = typeof body.section === 'string' && body.section.trim() ? body.section.trim() : null
  if (body.note !== undefined) bay.note = typeof body.note === 'string' && body.note.trim() ? body.note.trim() : null
  if (body.state === 'active' || body.state === 'out_of_service') bay.state = body.state
  return { bay, ...summariseBays(zoneId) }
}
export function demoDeleteZoneBay(zoneId: string, bayId: string) {
  const list = baysFor(zoneId || DEMO_ZONES[0].id)
  const i = list.findIndex(b => b.id === bayId)
  if (i >= 0 && !list[i].assigned) list.splice(i, 1)
  return summariseBays(zoneId)
}

// ─── Bay assignments (reserved bays phase 2) ────────────────────────────────
// Mirrors /api/permit-bays. Per-session store keyed by bay id → permit id; the
// seed reserves one Level 2 bay for the park-operations permit so the roster
// shows a reserved row without any clicking.
interface DemoBayAssignment { id: string; bayId: string; permitId: string; assignedAt: string; issuanceId?: string }
const assignmentStore = new Map<string, DemoBayAssignment>()
let assignmentsSeeded = false
function seedAssignments() {
  if (assignmentsSeeded) return
  assignmentsSeeded = true
  const bay = baysFor(ZONE_MAIN).find(b => b.label === '2-01')
  if (bay) { bay.assigned = true; assignmentStore.set(bay.id, { id: 'bas_demo_1', bayId: bay.id, permitId: 'perm_demo_001', assignedAt: isoDaysAgoAt(12, 10, 0) }) }
}
function allBays(): DemoZoneBay[] { return DEMO_ZONES.flatMap(z => baysFor(z.id)) }
function assignmentRows(filter: (a: DemoBayAssignment, bay: DemoZoneBay) => boolean) {
  seedAssignments()
  return [...assignmentStore.values()].flatMap(a => {
    const bay = allBays().find(b => b.id === a.bayId)
    if (!bay || !filter(a, bay)) return []
    return [{ id: a.id, bayId: bay.id, label: bay.label, section: bay.section, zoneId: bay.zoneId, bayState: bay.state, permitId: a.permitId, issuanceId: null, tenantId: null, actorEmail: 'demo@parkify.com', assignedAt: a.assignedAt }]
  }).sort((x, y) => x.label.localeCompare(y.label))
}
export function demoPermitBays(q: { permitId?: string | null; zoneId?: string | null }) {
  return { assignments: assignmentRows((a, bay) => (q.permitId ? a.permitId === q.permitId : true) && (q.zoneId ? bay.zoneId === q.zoneId : true)) }
}
export function demoAssignPermitBay(body: { permitId?: string; bayId?: string }) {
  seedAssignments()
  const bay = allBays().find(b => b.id === body.bayId)
  if (!bay || !body.permitId || bay.state !== 'active') return { error: 'Bay not found' }
  if (bay.assigned) return { error: `Bay ${bay.label} is assigned to another permit.`, heldBy: assignmentStore.get(bay.id)?.permitId ?? null }
  bay.assigned = true
  assignmentStore.set(bay.id, { id: `bas_demo_${Date.now().toString(36)}`, bayId: bay.id, permitId: body.permitId, assignedAt: new Date().toISOString() })
  return { assignments: assignmentRows(a => a.permitId === body.permitId) }
}
export function demoTransferPermitBay(body: { bayId?: string; toPermitId?: string }) {
  seedAssignments()
  const bay = allBays().find(b => b.id === body.bayId)
  const current = bay ? assignmentStore.get(bay.id) : undefined
  if (!bay || !current || !body.toPermitId) return { error: 'Bay is not assigned; assign it directly.' }
  assignmentStore.set(bay.id, { ...current, id: `bas_demo_${Date.now().toString(36)}`, permitId: body.toPermitId, assignedAt: new Date().toISOString() })
  return { assignments: assignmentRows(a => a.permitId === body.toPermitId), fromPermitId: current.permitId }
}
export function demoReleasePermitBay(permitId: string, bayId: string) {
  seedAssignments()
  const bay = allBays().find(b => b.id === bayId)
  if (bay && assignmentStore.get(bay.id)?.permitId === permitId) { assignmentStore.delete(bay.id); bay.assigned = false }
  return { assignments: assignmentRows(a => a.permitId === permitId) }
}
/** Permit ids → bay ids, so the demo permit list can carry the `bayIds` mirror. */
export function demoPermitBayIds(): Map<string, string[]> {
  seedAssignments()
  const m = new Map<string, string[]>()
  for (const a of assignmentStore.values()) m.set(a.permitId, [...(m.get(a.permitId) ?? []), a.bayId])
  return m
}

/** Pool bays for a program (phase 3): inventory rows joined with the demo assignment store. */
export function demoProgramPoolBays(allocations: { tenantId: string; bayIds?: string[] }[]) {
  seedAssignments()
  return allocations.flatMap(a => (a.bayIds ?? []).flatMap(id => {
    const bay = allBays().find(b => b.id === id)
    if (!bay) return []
    const held = assignmentStore.get(bay.id)
    return [{ bayId: bay.id, label: bay.label, section: bay.section, zoneId: bay.zoneId, state: bay.state, tenantId: a.tenantId, issuanceId: held?.issuanceId ?? null, permitId: held?.permitId ?? null }]
  }))
}
/** Consume a pool bay for a program seat in the demo; returns an error string when refused. */
export function demoTakePoolBay(bayId: string, permitId: string, issuanceId: string): string | null {
  seedAssignments()
  const bay = allBays().find(b => b.id === bayId)
  if (!bay || bay.state !== 'active') return 'That bay is out of service.'
  if (bay.assigned) return `Bay ${bay.label} is already taken.`
  bay.assigned = true
  assignmentStore.set(bay.id, { id: `bas_demo_${Date.now().toString(36)}`, bayId: bay.id, permitId, issuanceId, assignedAt: new Date().toISOString() })
  return null
}
/** Release whatever bay a program seat holds (revoke). */
export function demoReleaseSeatBay(issuanceId: string) {
  for (const [bayId, a] of assignmentStore) if (a.issuanceId === issuanceId) { assignmentStore.delete(bayId); const bay = allBays().find(b => b.id === bayId); if (bay) bay.assigned = false }
}

/**
 * `GET /api/zones/:id/platform-fee` — the zone's platform fee, read-only from
 * the signed agreement (LOC-22 PR4). The demo has no BILLING_DB, so every zone
 * shows the published Light price ($375 a month) as an active agreement with no
 * revenue share, which matches the demo's legacy fee terms (no warning).
 *
 * `?demoPlatformFee=none|canceled|mismatch|invoice` shows the other states the
 * console draws, like `demoReadinessState` above: no agreement on file, a
 * canceled one, an agreement whose 15% revenue share the zone's fee terms do
 * not carry (the staff-only warning), and an ACH-invoiced deal.
 * `enterprise` is a client marked Enterprise (LOC-22 PR5): no per-zone amount.
 */
export function demoZonePlatformFee(id: string): ZonePlatformFeeResponse | null {
  const zone = demoZoneById(id)
  if (!zone) return null
  const lens = typeof window !== 'undefined'
    ? new URL(window.location.href).searchParams.get('demoPlatformFee')
    : null
  const zoneCode = zone.displayName ?? zone.name
  const base: ZonePlatformFeeResponse = {
    zoneId: zone.id, zoneCode, source: 'signed_agreement', feeScope: lens === 'enterprise' ? 'enterprise' : 'zone', agreement: null,
  }
  if (lens === 'none' || lens === 'enterprise') return base
  return {
    ...base,
    agreement: {
      agreementId: `demo-${zone.id}`,
      status: lens === 'canceled' ? 'canceled' : 'active',
      dealType: 'platform_fee',
      feeHandling: lens === 'invoice' ? 'ach_invoice' : 'stripe_subscription',
      monthlyCents: 37500,
      serviceStartDate: dateOnlyDaysFromNow(-60),
      parkingRevenueShareBps: lens === 'mismatch' ? 1500 : 0,
    },
  }
}

export { ZONE_MAIN, ZONE_NORTH, ZONE_VIP }
export type { DemoZoneMeta }
export { isoDaysAgoAt }
